import React, { useEffect, useState, useCallback } from "react";
import { Navigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Store, Star, Mail, MapPin, Plus, Pencil, Trash2, X, Send, Copy, Check, Inbox } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const emptyForm = { name: "", category: "bonding", tagline: "", location: "", rating: 4.5, contact: "" };

export default function Marketplace() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [active, setActive] = useState("");
  const [editing, setEditing] = useState(null); // provider or 'new'
  const [form, setForm] = useState(emptyForm);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteResult, setInviteResult] = useState(null);
  const [inviteBusy, setInviteBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [subs, setSubs] = useState([]);

  const load = useCallback(() => {
    api.get(`/marketplace${active ? `?category=${active}` : ""}`).then((r) => {
      setData(r.data);
      if (r.data.is_admin) api.get("/marketplace/submissions").then((s) => setSubs(s.data.submissions || [])).catch(() => {});
    });
  }, [active]);
  useEffect(() => { load(); }, [load]);

  // Admin-only gate — marketplace data is still placeholder / not vetted.
  // Placed after all hooks so React's rules of hooks aren't violated.
  if (user && !user.is_platform_admin) return <Navigate to="/dashboard" replace />;

  const openNew = () => { setForm(emptyForm); setEditing("new"); };
  const openEdit = (p) => { setForm({ name: p.name, category: p.category, tagline: p.tagline, location: p.location, rating: p.rating, contact: p.contact }); setEditing(p); };

  const save = async () => {
    try {
      if (editing === "new") await api.post("/marketplace", form);
      else await api.patch(`/marketplace/${editing.id}`, form);
      toast.success("Saved"); setEditing(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
  };
  const remove = async (p) => {
    try { await api.delete(`/marketplace/${p.id}`); toast.success("Removed"); load(); }
    catch (e) { toast.error("Could not remove"); }
  };

  const sendInvite = async () => {
    setInviteBusy(true); setInviteResult(null); setCopied(false);
    try {
      const r = await api.post("/marketplace/invite", { email: inviteEmail, origin_url: window.location.origin });
      setInviteResult(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not create invite"); }
    finally { setInviteBusy(false); }
  };
  const copyLink = () => { navigator.clipboard.writeText(inviteResult.link); setCopied(true); setTimeout(() => setCopied(false), 2000); };
  const closeInvite = () => { setInviteOpen(false); setInviteEmail(""); setInviteResult(null); };
  const approveSub = async (t) => { try { await api.post(`/marketplace/submissions/${t}/approve`); toast.success("Approved & published"); load(); } catch { toast.error("Could not approve"); } };
  const rejectSub = async (t) => { try { await api.post(`/marketplace/submissions/${t}/reject`); toast.success("Rejected"); load(); } catch { toast.error("Could not reject"); } };

  if (!data) return <div className="h-64 flex items-center justify-center"><div className="h-8 w-8 border-[3px] border-slate-200 border-t-slate-900 rounded-full animate-spin" /></div>;

  // Locked for regular users until the first real provider is added (admins always see full UI).
  if (!data.has_providers && !data.is_admin) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center" data-testid="marketplace-coming-soon">
        <div className="max-w-lg text-center">
          <div className="mx-auto h-16 w-16 rounded-2xl bg-emerald-900 text-white flex items-center justify-center mb-6">
            <Store className="h-8 w-8" />
          </div>
          <span className="inline-block text-xs font-bold uppercase tracking-wider bg-lime-100 text-emerald-800 rounded-full px-3 py-1 mb-4">Coming soon</span>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-slate-900">Partner Marketplace</h1>
          <p className="text-slate-500 mt-3 text-base leading-relaxed">
            We're building a network of vetted bonding, insurance, financing, CPA, legal, staffing, and estimating partners to help you qualify, finance, and win bigger contracts. Check back soon.
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-2">
            {data.categories.map((c) => (
              <span key={c.id} className="text-xs font-medium px-3 py-1.5 rounded-full bg-white border border-slate-200 text-slate-500">{c.label}</span>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="marketplace-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight inline-flex items-center gap-2"><Store className="h-7 w-7" /> Marketplace</h1>
          <p className="text-slate-500 mt-1">Vetted partners to help you qualify, finance, and win bigger contracts.</p>
        </div>
        {data.is_admin && (
          <div className="flex items-center gap-2">
            <button onClick={() => setInviteOpen(true)} data-testid="invite-partner-button" className="bg-white border border-slate-200 text-slate-700 rounded-md px-4 py-2 text-sm font-medium hover:bg-slate-50 transition-colors inline-flex items-center gap-2">
              <Send className="h-4 w-4" /> Invite a partner
            </button>
            <button onClick={openNew} data-testid="add-provider-button" className="bg-emerald-900 text-white rounded-md px-4 py-2 text-sm font-medium hover:bg-emerald-800 transition-colors inline-flex items-center gap-2">
              <Plus className="h-4 w-4" /> Add provider
            </button>
          </div>
        )}
      </div>

      {data.is_admin && subs.length > 0 && (
        <div className="rounded-xl border border-amber-200 bg-amber-50/50 p-5" data-testid="pending-submissions">
          <p className="font-heading font-semibold text-slate-900 inline-flex items-center gap-2 mb-3"><Inbox className="h-5 w-5 text-amber-600" /> Pending partner applications ({subs.length})</p>
          <div className="space-y-2">
            {subs.map((s) => (
              <div key={s.token} className="bg-white border border-slate-200 rounded-lg p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-testid={`submission-${s.token}`}>
                <div className="min-w-0">
                  <p className="font-medium text-slate-900">{s.submission?.name} <span className="text-xs font-normal text-slate-400">· {data.categories.find((c) => c.id === s.submission?.category)?.label || s.submission?.category}</span></p>
                  <p className="text-sm text-slate-500 truncate">{s.submission?.tagline}</p>
                  <p className="text-xs text-slate-400 mt-0.5">{[s.submission?.location, s.submission?.contact, s.submission?.website].filter(Boolean).join(" · ")}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button onClick={() => approveSub(s.token)} data-testid={`approve-${s.token}`} className="bg-emerald-900 text-white rounded-md px-3 py-1.5 text-sm font-medium hover:bg-emerald-800 transition-colors">Approve</button>
                  <button onClick={() => rejectSub(s.token)} data-testid={`reject-${s.token}`} className="bg-white border border-slate-200 text-slate-600 rounded-md px-3 py-1.5 text-sm font-medium hover:bg-slate-50 transition-colors">Reject</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        <button onClick={() => setActive("")} data-testid="filter-all" className={`text-sm font-medium rounded-full px-4 py-1.5 border transition-colors ${active === "" ? "bg-emerald-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"}`}>All</button>
        {data.categories.map((c) => (
          <button key={c.id} onClick={() => setActive(c.id)} data-testid={`filter-${c.id}`} className={`text-sm font-medium rounded-full px-4 py-1.5 border transition-colors ${active === c.id ? "bg-emerald-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"}`}>{c.label}</button>
        ))}
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {data.providers.map((p) => (
          <div key={p.id} className="bg-white border border-slate-200 rounded-xl shadow-sm p-6 flex flex-col hover:shadow-md transition-all group" data-testid={`provider-${p.id}`}>
            <div className="flex items-start justify-between">
              <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-slate-100 text-slate-600">{data.categories.find((c) => c.id === p.category)?.label || p.category}</span>
              <span className="inline-flex items-center gap-1 text-sm font-data text-amber-500"><Star className="h-4 w-4 fill-amber-400 text-amber-400" />{p.rating}</span>
            </div>
            <h3 className="font-heading text-lg font-semibold mt-3">{p.name}</h3>
            <p className="text-sm text-slate-600 mt-1 flex-1">{p.tagline}</p>
            <div className="flex flex-wrap gap-3 mt-4 text-xs text-slate-500">
              {p.location && <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{p.location}</span>}
              {p.contact && <span className="inline-flex items-center gap-1"><Mail className="h-3.5 w-3.5" />{p.contact}</span>}
            </div>
            {data.is_admin && (
              <div className="flex gap-2 mt-4 pt-4 border-t border-slate-100">
                <button onClick={() => openEdit(p)} data-testid={`edit-provider-${p.id}`} className="text-xs text-slate-600 hover:text-slate-900 inline-flex items-center gap-1"><Pencil className="h-3.5 w-3.5" /> Edit</button>
                <button onClick={() => remove(p)} data-testid={`delete-provider-${p.id}`} className="text-xs text-slate-400 hover:text-red-500 inline-flex items-center gap-1"><Trash2 className="h-3.5 w-3.5" /> Remove</button>
              </div>
            )}
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-emerald-900/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setEditing(null)}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-7" onClick={(e) => e.stopPropagation()} data-testid="provider-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading text-xl font-semibold">{editing === "new" ? "Add provider" : "Edit provider"}</h3>
              <button onClick={() => setEditing(null)} className="text-slate-400 hover:text-slate-900"><X className="h-5 w-5" /></button>
            </div>
            <div className="space-y-3">
              {[["name", "Name"], ["tagline", "Tagline"], ["location", "Location"], ["contact", "Contact email"]].map(([k, l]) => (
                <input key={k} data-testid={`provider-${k}`} placeholder={l} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent/40" />
              ))}
              <select data-testid="provider-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm">
                {data.categories.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
              </select>
              <input type="number" step="0.1" min="0" max="5" data-testid="provider-rating" placeholder="Rating" value={form.rating} onChange={(e) => setForm({ ...form, rating: +e.target.value })} className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm" />
            </div>
            <button onClick={save} data-testid="save-provider-button" className="w-full mt-5 bg-emerald-900 text-white rounded-md py-2.5 text-sm font-medium hover:bg-emerald-800 transition-colors">Save</button>
          </div>
        </div>
      )}

      {inviteOpen && (
        <div className="fixed inset-0 z-50 bg-emerald-900/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={closeInvite}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-7" onClick={(e) => e.stopPropagation()} data-testid="invite-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading text-xl font-semibold inline-flex items-center gap-2"><Send className="h-5 w-5" /> Invite a partner</h3>
              <button onClick={closeInvite} className="text-slate-400 hover:text-slate-900"><X className="h-5 w-5" /></button>
            </div>
            {!inviteResult ? (
              <>
                <p className="text-sm text-slate-500 mb-4">Enter the partner's email. We'll generate a private application link (and email it to them) so they can submit their listing for your approval.</p>
                <input data-testid="invite-email" type="email" placeholder="partner@company.com" value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} className="w-full rounded-md border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-accent/40" />
                <button onClick={sendInvite} disabled={!inviteEmail || inviteBusy} data-testid="send-invite-button" className="w-full mt-4 bg-emerald-900 text-white rounded-md py-2.5 text-sm font-medium hover:bg-emerald-800 disabled:opacity-60 transition-colors">
                  {inviteBusy ? "Creating…" : "Create invite link"}
                </button>
              </>
            ) : (
              <div data-testid="invite-result">
                <p className="text-sm text-slate-600 mb-2">
                  {inviteResult.emailed ? "✅ Invite emailed. You can also share this link directly:" : "Invite link created. Email delivery isn't active yet — copy and send this link to your partner:"}
                </p>
                <div className="flex items-center gap-2">
                  <input readOnly value={inviteResult.link} data-testid="invite-link" className="flex-1 rounded-md border border-slate-200 px-3 py-2 text-xs bg-slate-50 text-slate-600" />
                  <button onClick={copyLink} data-testid="copy-invite-link" className="shrink-0 bg-slate-900 text-white rounded-md px-3 py-2 text-sm inline-flex items-center gap-1.5">
                    {copied ? <><Check className="h-4 w-4" /> Copied</> : <><Copy className="h-4 w-4" /> Copy</>}
                  </button>
                </div>
                <button onClick={closeInvite} className="w-full mt-4 border border-slate-200 rounded-md py-2 text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors">Done</button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
